/**
 * Sortable table header cell.
 *
 * `sortable` defaults to `false` so this package stays usable by both the admin
 * site and the three end-user portals: when off, the component renders exactly
 * the static `TableHead` it did before. Only turn it on where the backend really
 * honours the sort key — a clickable header over data that never moves is worse
 * than no control at all.
 */
import React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../cn';
import { TableHead } from './Table';
import { ariaSortValue, sortButtonAriaLabel } from './sortState';
const SortableTableHead = ({ sortable = false, label, sortKey, activeKey = null, direction = null, onSort, align = 'left', magnitude = false, defaultDirection = 'DESC', disabled = false, className, children, ...props }) => {
    if (!sortable || !onSort) {
        return (React.createElement(TableHead, { className: className, ...props }, children !== null && children !== void 0 ? children : label));
    }
    const isActive = sortKey === activeKey && Boolean(direction);
    const ariaSort = ariaSortValue(sortKey, activeKey, direction);
    const Icon = !isActive ? ChevronsUpDown : direction === 'ASC' ? ArrowUp : ArrowDown;
    return (React.createElement(TableHead, { className: cn(align === 'right' ? 'text-right' : 'text-left', className), "aria-sort": ariaSort, ...props },
        React.createElement("button", { type: "button", onClick: () => onSort(sortKey), disabled: disabled, "aria-label": sortButtonAriaLabel({
                label: typeof label === 'string' ? label : sortKey,
                columnKey: sortKey,
                activeKey,
                direction,
                magnitude,
                defaultDirection,
            }), className: cn('group inline-flex max-w-full items-center gap-1 rounded py-1', 'font-medium transition-colors', 'hover:text-foreground focus-visible:outline-none', 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1', 'disabled:cursor-not-allowed disabled:opacity-60', isActive ? 'text-foreground' : 'text-muted-foreground') },
            React.createElement("span", { className: "inline-flex items-baseline gap-1" }, label),
            React.createElement(Icon, { "aria-hidden": true, className: cn('h-3.5 w-3.5 shrink-0 transition-opacity', isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-100 group-focus-visible:opacity-100') }))));
};
export default SortableTableHead;
